import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleButton } from '@/components/google-button';
import { Logo } from '@/components/brand/logo';
import { AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import i18n, { currentLocale, LOCALES, setLocale } from '@/lib/i18n';
import type { TenantChoice } from '@/lib/schemas';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { request } from '@/lib/api';
import { z as zod } from 'zod';
import { MfaStep } from './mfa-step';

const schema = z.object({
  email: z.string().min(1, i18n.t('auth:validation.emailRequired')).email(i18n.t('auth:validation.invalidEmail')),
  password: z.string().min(1, i18n.t('auth:validation.passwordRequired')),
});
type FormValues = z.infer<typeof schema>;

const GOOGLE_ERRORS: Record<string, 'google.cancelled' | 'google.failed'> = {
  cancelled: 'google.cancelled',
  failed: 'google.failed',
};

export function LoginPage() {
  const { t } = useTranslation('auth');
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  // ה-guard של המסלול שומר לאן המשתמש ניסה להגיע לפני שהופנה לכאן,
  // כדי שהתחברות תחזיר אותו לשם ולא למסך הבית.
  const search = useSearch({ strict: false });
  const [serverError, setServerError] = useState<string | null>(null);
  // אותו אימייל בכמה עסקים (בסיסמה או ב-Google): בוחרים לאן להיכנס.
  const [choices, setChoices] = useState<{
    tenants: TenantChoice[];
    pick: (subdomain: string) => Promise<void>;
  } | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  // 2FA: הסיסמה (או Google) אומתה, ועכשיו קוד. הכרטיס קצר וחד-פעמי.
  const [mfaTicket, setMfaTicket] = useState<string | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(
    search.google_error ? t(GOOGLE_ERRORS[search.google_error] ?? 'google.failed') : null,
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { email: '', password: '' } });

  const enter = async (values: FormValues, tenant?: string) => {
    setServerError(null);
    try {
      const step = await login(values.email, values.password, tenant);
      if (step.kind === 'chooseTenant') {
        setChoices({ tenants: step.tenants, pick: (subdomain) => enter(values, subdomain) });
        return;
      }
      if (step.kind === 'mfa') {
        setChoices(null);
        setMfaTicket(step.ticket);
        return;
      }
      // הניווט חייב לקרות כאן במפורש: עדכון ה-state לבדו לא מזיז
      // את ה-router, והמשתמש היה נשאר תקוע במסך ההתחברות אחרי
      // התחברות מוצלחת.
      await navigate({ to: search.redirect ?? '/', replace: true });
    } catch (err) {
      // השרת מחזיר הודעה אחידה לשם משתמש לא קיים ולסיסמה שגויה —
      // אין למסור כאן מידע שמאפשר לגלות אילו חשבונות קיימים.
      setServerError(err instanceof ApiError ? err.message : t('login.failed'));
    }
  };
  const onSubmit = handleSubmit((values) => enter(values));

  // חזרה מ-Google עם כרטיס: מחליפים בסשן פעם אחת, ומנקים אותו מהכתובת.
  const exchanged = useRef(false);
  useEffect(() => {
    const ticket = search.google_ticket;
    if (!ticket || exchanged.current) return;
    exchanged.current = true;
    const redirectTo = search.redirect ?? '/';
    const viaGoogle = async (current: string, subdomain?: string) => {
      setGoogleError(null);
      setGoogleBusy(true);
      try {
        const step = await loginWithGoogle(current, subdomain);
        if (step.kind === 'chooseTenant') {
          const next = step.ticket ?? current;
          setChoices({ tenants: step.tenants, pick: (s) => viaGoogle(next, s) });
          return;
        }
        if (step.kind === 'mfa') {
          setChoices(null);
          setMfaTicket(step.ticket);
          return;
        }
        await navigate({ to: redirectTo, replace: true });
      } catch (err) {
        setGoogleError(
          err instanceof ApiError && err.status === 401
            ? t('google.noAccount')
            : t('google.failed'),
        );
      } finally {
        setGoogleBusy(false);
      }
    };
    void navigate({ to: '/login', search: { redirect: search.redirect }, replace: true });
    void viaGoogle(ticket);
  }, [search.google_ticket, search.redirect, loginWithGoogle, navigate, t]);

  const startGoogle = async () => {
    setGoogleError(null);
    setGoogleBusy(true);
    try {
      const { authUrl } = await request('/auth/google/start', {
        tenant: null,
        schema: zod.object({ authUrl: zod.string().url() }),
      });
      window.location.assign(authUrl);
    } catch (err) {
      setGoogleBusy(false);
      setGoogleError(
        err instanceof ApiError && err.status === 503
          ? t('google.notConfigured')
          : t('google.failed'),
      );
    }
  };

  return (
    <div className="grid min-h-dvh place-items-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <h1 className="sr-only">CraftMind AI</h1>
          <Logo size="lg" />
          <p className="text-sm text-fg-muted">{t('login.subtitle')}</p>
        </div>

        {mfaTicket ? (
          <MfaStep
            ticket={mfaTicket}
            onDone={() => navigate({ to: search.redirect ?? '/', replace: true })}
            onCancel={() => setMfaTicket(null)}
          />
        ) : (
        <form
          onSubmit={(e) => void onSubmit(e)}
          noValidate
          className="space-y-4 rounded-(--radius-xl) border border-border bg-surface-raised p-6 shadow-sm"
        >
          {serverError ? (
            // role="alert" — קורא מסך מכריז על השגיאה מיד, בלי לחכות
            // שהמשתמש ינווט אליה.
            <div
              role="alert"
              className="flex items-start gap-2 rounded-(--radius-md) border border-danger-border bg-danger-subtle p-3 text-xs text-danger"
            >
              <AlertCircle className="mt-px size-4 shrink-0" aria-hidden />
              <span>{serverError}</span>
            </div>
          ) : null}

          <Field label={t('login.email')} error={errors.email?.message} htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="username"
              dir="ltr"
              invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              {...register('email')}
            />
          </Field>

          <Field label={t('login.password')} error={errors.password?.message} htmlFor="password">
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              {...register('password')}
            />
          </Field>

          <Button type="submit" loading={isSubmitting} className="w-full" size="lg">
            {t('login.submit')}
          </Button>

          <div className="flex items-center gap-3 text-2xs text-fg-subtle" aria-hidden>
            <span className="h-px flex-1 bg-border" />
            {t('login.or')}
            <span className="h-px flex-1 bg-border" />
          </div>

          {googleError ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-(--radius-md) border border-danger-border bg-danger-subtle p-3 text-xs text-danger"
            >
              <AlertCircle className="mt-px size-4 shrink-0" aria-hidden />
              <span>{googleError}</span>
            </div>
          ) : null}

          <GoogleButton
            busy={googleBusy}
            label={googleBusy ? t('google.connecting') : t('google.button')}
            onClick={() => void startGoogle()}
          />

          {choices ? (
            <fieldset className="space-y-2 border-t border-border pt-4">
              <legend className="mb-2 text-sm font-medium text-fg">{t('login.pickTenant')}</legend>
              {choices.tenants.map((t) => (
                <Button
                  key={t.subdomain}
                  type="button"
                  variant="secondary"
                  className="w-full justify-between"
                  onClick={() => void choices.pick(t.subdomain)}
                >
                  {t.name}
                  <span dir="ltr" className="text-xs text-fg-subtle">
                    {t.subdomain}
                  </span>
                </Button>
              ))}
            </fieldset>
          ) : null}
        </form>
        )}

        <p className="mt-5 text-center text-xs text-fg-muted">
          {t('login.newBusiness')}{' '}
          <Link to="/onboarding" className="font-medium text-accent hover:underline">
            {t('login.createBusiness')}
          </Link>
        </p>
        <p className="mt-3 flex justify-center gap-3 text-2xs text-fg-subtle">
          <Link to="/about" className="hover:underline">
            {t('login.about')}
          </Link>
          <Link to="/privacy" className="hover:underline">
            {t('login.privacy')}
          </Link>
          <Link to="/terms" className="hover:underline">
            {t('login.terms')}
          </Link>
        </p>
        <LanguageToggle />
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  htmlFor,
  children,
}: {
  label: string;
  error?: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-xs font-medium text-fg-muted">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} className="text-2xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * כפתור לפי הנחיות המיתוג של Google ("Sign in with Google"): רקע לבן, מסגרת
 * אפורה, הלוגו הצבעוני בשמאל ובגודל המקורי. Google בודקת את זה באימות האפליקציה.
 */
/** לפני התחברות אין עסק — השפה נשמרת בדפדפן הזה (setLocale טוען מחדש). */
function LanguageToggle() {
  const { t } = useTranslation(['auth', 'common']);
  const active = currentLocale();
  return (
    <div role="group" aria-label={t('login.languageLabel')} className="mt-3 flex justify-center gap-1 text-2xs">
      {LOCALES.map((l, i) => (
        <span key={l} className="flex items-center gap-1">
          {i > 0 ? (
            <span className="text-fg-subtle" aria-hidden>
              |
            </span>
          ) : null}
          <button
            type="button"
            lang={l}
            aria-pressed={l === active}
            onClick={() => setLocale(l)}
            className={l === active ? 'font-medium text-fg' : 'text-fg-subtle hover:underline'}
          >
            {t(`common:language.${l}`)}
          </button>
        </span>
      ))}
    </div>
  );
}
